import { useEffect, useRef, useImperativeHandle, forwardRef, useState } from "react";
import { createChart, CandlestickSeries, LineSeries, createSeriesMarkers } from "lightweight-charts";

// Generic candlestick chart wrapper with imperative API + draggable SL/TP support
const Chart = forwardRef(function Chart({ height = 480, smt = false, onDragLine, onMarkerClick }, ref) {
  const wrapRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const markersRef = useRef(null);
  const overlaySeriesRef = useRef([]);
  const priceLineRefs = useRef([]);
  const draggableRef = useRef({ entry: null, sl: null, tp: null });
  const dragRef = useRef(null);
  const currentMarkersRef = useRef([]);
  // Stable callback refs so we don't rebuild the chart when parent passes a new closure
  const onDragLineRef = useRef(onDragLine);
  const onMarkerClickRef = useRef(onMarkerClick);
  useEffect(() => { onDragLineRef.current = onDragLine; }, [onDragLine]);
  useEffect(() => { onMarkerClickRef.current = onMarkerClick; }, [onMarkerClick]);
  const [, force] = useState(0);

  useEffect(() => {
    if (!wrapRef.current) return;
    const chart = createChart(wrapRef.current, {
      autoSize: true,
      localization: { locale: "en-US" },
      layout: { background: { color: "#090b0f" }, textColor: "#94a3b8", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.03)" },
        horzLines: { color: "rgba(255,255,255,0.04)" },
      },
      rightPriceScale: { borderColor: "#1f2937" },
      timeScale: { borderColor: "#1f2937", timeVisible: true, secondsVisible: false },
      crosshair: { mode: 1 },
    });
    const series = chart.addSeries(CandlestickSeries, smt ? {
      upColor: "#f472b6", downColor: "#e11d48",
      borderUpColor: "#f472b6", borderDownColor: "#e11d48",
      wickUpColor: "#f472b6", wickDownColor: "#e11d48",
      priceLineVisible: false, lastValueVisible: false,
    } : {
      upColor: "#10b981", downColor: "#f43f5e",
      borderUpColor: "#10b981", borderDownColor: "#f43f5e",
      wickUpColor: "#10b981", wickDownColor: "#f43f5e",
    });
    chartRef.current = chart;
    seriesRef.current = series;
    markersRef.current = createSeriesMarkers(series, []);

    // ---- Marker click detection ----
    // lightweight-charts doesn't expose per-marker click events, so we listen
    // to chart clicks, take the clicked time, and resolve to the nearest marker.
    const handleClick = (param) => {
      const cb = onMarkerClickRef.current;
      const markers = currentMarkersRef.current;
      if (!cb || !markers.length || !param?.time) return;
      // Find marker within ±1 bar of the click
      let closest = null, closestDiff = Infinity;
      for (const m of markers) {
        const diff = Math.abs(m.time - param.time);
        if (diff < closestDiff) { closestDiff = diff; closest = m; }
      }
      if (closest) cb(closest, param);
    };
    chart.subscribeClick(handleClick);

    // ---- Drag handling for SL/TP via mouse events on container ----
    if (!smt) {
      const el = wrapRef.current;
      const yToPrice = (clientY) => {
        const rect = el.getBoundingClientRect();
        const y = clientY - rect.top;
        return series.coordinateToPrice(y);
      };
      const priceToY = (price) => series.priceToCoordinate(price);

      const onDown = (e) => {
        const lines = draggableRef.current;
        if (!lines.sl && !lines.tp) return;
        const rect = el.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const slY = lines.sl != null ? priceToY(lines.sl) : null;
        const tpY = lines.tp != null ? priceToY(lines.tp) : null;
        const tol = 10;
        let hit = null;
        if (slY != null && Math.abs(y - slY) < tol) hit = "sl";
        else if (tpY != null && Math.abs(y - tpY) < tol) hit = "tp";
        if (hit) {
          dragRef.current = hit;
          el.style.cursor = "ns-resize";
          // Freeze chart pan/zoom during line drag so the chart doesn't scroll with the mouse
          chart.applyOptions({
            handleScroll: { mouseWheel: false, pressedMouseMove: false, horzTouchDrag: false, vertTouchDrag: false },
            handleScale:  { axisPressedMouseMove: false, mouseWheel: false, pinch: false },
          });
          e.preventDefault(); e.stopPropagation();
        }
      };
      const onMove = (e) => {
        const lines = draggableRef.current;
        const rect = el.getBoundingClientRect();
        const y = e.clientY - rect.top;
        // hover cursor
        if (!dragRef.current && (lines.sl != null || lines.tp != null)) {
          const slY = lines.sl != null ? priceToY(lines.sl) : null;
          const tpY = lines.tp != null ? priceToY(lines.tp) : null;
          if ((slY != null && Math.abs(y - slY) < 8) || (tpY != null && Math.abs(y - tpY) < 8)) {
            el.style.cursor = "ns-resize";
          } else el.style.cursor = "";
        }
        if (!dragRef.current) return;
        const p = yToPrice(e.clientY);
        if (p == null) return;
        onDragLineRef.current && onDragLineRef.current(dragRef.current, p);
      };
      const onUp = () => {
        if (dragRef.current) {
          el.style.cursor = "";
          dragRef.current = null;
          // Restore chart interactions
          chart.applyOptions({
            handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: true },
            handleScale:  { axisPressedMouseMove: true, mouseWheel: true, pinch: true },
          });
        }
      };

      el.addEventListener("mousedown", onDown);
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      chart._cleanup = () => {
        el.removeEventListener("mousedown", onDown);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
    }
    force(n => n + 1);
    return () => {
      try { chart._cleanup && chart._cleanup(); } catch {}
      try { chart.remove(); } catch {}
    };
  }, [smt]); // <- removed onDragLine; using ref so chart is created once per mount

  useImperativeHandle(ref, () => ({
    setData(bars) { if (seriesRef.current) seriesRef.current.setData(bars); },
    update(bar)   { if (seriesRef.current) seriesRef.current.update(bar); },
    setMarkers(markers) {
      currentMarkersRef.current = markers || [];
      if (markersRef.current) markersRef.current.setMarkers(markers);
    },
    fitContent() { chartRef.current?.timeScale().fitContent(); },
    resetView() {
      try {
        chartRef.current?.timeScale().resetTimeScale();
        chartRef.current?.timeScale().fitContent();
        chartRef.current?.priceScale("right").applyOptions({ autoScale: true });
      } catch {}
    },
    scrollToTime(t) {
      try { chartRef.current?.timeScale().scrollToPosition(0, false); chartRef.current?.timeScale().setVisibleRange({ from: t - 3600, to: t + 3600 }); } catch {}
    },
    setVisibleRange(from, to) {
      try { chartRef.current?.timeScale().setVisibleRange({ from, to }); } catch {}
    },
    setDraggable({ entry, sl, tp }) {
      draggableRef.current = { entry, sl, tp };
    },
    clearPriceLines() {
      priceLineRefs.current.forEach(pl => { try { seriesRef.current.removePriceLine(pl); } catch {} });
      priceLineRefs.current = [];
    },
    addPriceLine(opts) {
      if (!seriesRef.current) return;
      const pl = seriesRef.current.createPriceLine(opts);
      priceLineRefs.current.push(pl);
      return pl;
    },
    addLineOverlay(points, color) {
      if (!chartRef.current) return null;
      const sorted = [...points]
        .sort((a, b) => a.time - b.time)
        .filter((p, i, arr) => i === 0 || p.time !== arr[i - 1].time);
      if (sorted.length < 2) return null;
      const ls = chartRef.current.addSeries(LineSeries, { color, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false });
      ls.setData(sorted);
      overlaySeriesRef.current.push(ls);
      return ls;
    },
    clearOverlays() {
      overlaySeriesRef.current.forEach(s => { try { chartRef.current.removeSeries(s); } catch {} });
      overlaySeriesRef.current = [];
    },
  }));

  return <div ref={wrapRef} style={{ width: "100%", height }} data-testid={smt ? "smt-chart" : "main-chart"} />;
});

export default Chart;
